import {
  Alert,
  Button,
  Col,
  Dropdown,
  Form,
  Modal,
  Row,
  Space,
  Typography,
  message,
} from "antd";
import DynamicForm from "../controls/DynamicForm";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import Visual from "../controls/visual";
import { QueryOperation } from "../code/search-query/search-query";
import View from "../controls/View";
import { CompanyUser, MAX_PAGE_SIZE } from "../repository/data.default";
// NOTE: Will need it for future use
// import AssignRewardFormDialog from "./AssignRewardFormDialog";
// import CalendarEventDialog from "./CalendarEventDialog";
// import ResetUserFormDialog from "./ResetUserFormDialog";
import {
  deleteCompanyUser,
  getCompanyUser,
  getUserLoginHistory,
} from "../repository/data.repository";
import { getProblemMessage } from "../utils/http";

export interface CompanyUserViewProps {
  readonly open?: boolean;
  readonly mode: "view" | "delete";
  readonly uid?: string;
  readonly onFinish?: () => void;
  readonly onClose?: () => void;
}

function CompanyUserView(props: CompanyUserViewProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<{
    [key: string]: number;
  }>({});
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    "Something went wrong, try again",
  );
  // NOTE: Will need it for future use
  const [inputMode, setInputMode] = useState<
    "resetPassword" | "assignReward" | "scheduleEvent"
  >("assignReward");
  // NOTE: Will need it for future use
  const [inputOpen, setInputOpen] = useState(false);
  const [item, setItem] = useState<CompanyUser | null>(null);

  // Lifecycle

  useEffect(() => {
    if (props.open) {
      loadData();
    } else {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.uid]);

  // Events
  function onInputOpen(
    mode: "resetPassword" | "assignReward" | "scheduleEvent",
  ): void {
    setInputMode(mode);
    setInputOpen(true);
    message.info("This action is not yet available.");
  }

  // NOTE: Will need it for future use
  function onInputClose(): void {
    setInputOpen(false);
  }

  function onSubmit(): void {
    setLoading(true);
    setError(false);
    if (props.mode === "delete") {
      deleteCompanyUser(props?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((e: any) => {
          setErrorMessage(getProblemMessage(e));
          setLoading(false);
          setError(true);
        });
    }
  }

  function onClose(): void {
    props.onClose?.();
  }

  // Methods
  function loadData(): void {
    if (props?.uid) {
      setLoading(true);
      Promise.all([
        getCompanyUser(props?.uid!),
        getUserLoginHistory({
          searchQuery: {
            conditions: [
              {
                field: "userUid",
                operation: QueryOperation.Equal,
                values: [props?.uid],
              },
              {
                field: "timestamp",
                operation: QueryOperation.Between,
                values: [
                  dayjs().subtract(90, "days").format("YYYY-MM-DDTHH:mm:ssZ"),
                  dayjs().format("YYYY-MM-DDTHH:mm:ssZ"),
                ],
              },
            ],
          },
          pageNumber: 0,
          pageSize: MAX_PAGE_SIZE,
        }),
      ])
        .then(([item, history]) => {
          setLoading(false);
          setItem(item);
          form.setFieldsValue(item);
          const date = history.data.reduce((acc, item) => {
            const date = dayjs(item.timestamp).format("YYYY-MM-DD");
            acc[date] = (acc[date] ?? 0) + 1;

            return acc;
          }, {} as any);
          setHistory(date);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }

  function createMode(): string {
    switch (props.mode) {
      case "delete":
        return "Delete";
    }

    return "";
  }

  function reset(): void {
    setError(false);
    setLoading(false);
    form.resetFields();
  }

  return (
    <>
      <View
        open={["view"].includes(props.mode) && props.open}
        loading={loading}
        title={"User"}
        actions={
          <Space className="flex flex-row">
            <Dropdown.Button
              type="primary"
              menu={{
                items: [
                  {
                    key: "assignReward",
                    label: "Assign Reward",
                    onClick: () => onInputOpen("assignReward"),
                  },
                  {
                    key: "resetPassword",
                    label: "Reset Password",
                    danger: true,
                    disabled: !item?.email,
                    onClick: () => onInputOpen("resetPassword"),
                  },
                ],
              }}
              onClick={() => onInputOpen("scheduleEvent")}
            >
              Schedule Event
            </Dropdown.Button>
          </Space>
        }
        onBack={onClose}
      >
        {props.mode === "view" && (
          <Row className="flex flex-row flex-wrap">
            <DynamicForm
              form={form}
              preview
              schema={{
                groups: [
                  {
                    controls: [
                      {
                        type: "labelbox",
                        label: "Name",
                        name: "firstName",
                      },
                      {
                        type: "labelbox",
                        label: "Middle Name",
                        name: "middleName",
                      },
                      {
                        type: "labelbox",
                        label: "Last Name",
                        name: "lastName",
                      },
                      {
                        type: "labelbox",
                        label: "Email",
                        name: "email",
                      },
                      {
                        type: "labelbox",
                        label: "Date Of Birth",
                        name: "dateOfBirth",
                        format: (value) => dayjs(value).format("DD/MM/YYYY"),
                      },
                      {
                        type: "labelbox",
                        label: "Company",
                        name: "companyName",
                      },
                      {
                        type: "labelbox",
                        label: "Tenant",
                        name: "tenantName",
                      },
                      {
                        type: "labelbox",
                        label: "Department",
                        name: "departmentName",
                      },
                      {
                        type: "labelbox",
                        label: "Vessel",
                        name: "vesselName",
                      },
                      {
                        type: "labelbox",
                        label: "Last Login",
                        name: "lastLoginAt",
                      },
                      {
                        type: "labelbox",
                        label: "Role Name",
                        name: "companyRoleName",
                      },
                      {
                        type: "labelbox",
                        label: "Joined Date",
                        name: "joinedDate",
                        format: (value) =>
                          dayjs(value).format("DD/MM/YYYY HH:mm"),
                      },
                      {
                        type: "labelbox",
                        label: "Company Phone",
                        name: "companyPhoneNumber",
                      },
                      {
                        type: "labelbox",
                        label: "About Me",
                        name: "aboutMe",
                      },
                      {
                        type: "labelbox",
                        label: "Gender",
                        name: "gender",
                      },
                      {
                        type: "labelbox",
                        label: "Test Language Name",
                        name: "testLanguageName",
                      },
                      {
                        type: "labelbox",
                        label: "Country",
                        name: "countryName",
                      },
                      {
                        type: "labelbox",
                        label: "Created On",
                        name: "createdOn",
                        format: (value) =>
                          dayjs(value).format("DD/MM/YYYY HH:mm"),
                      },
                    ],
                  },
                ],
              }}
            />
            <Visual
              data={history}
              width="auto"
              render={(chart, data) => {
                chart
                  .data(
                    Object.entries(data).map(([days, logins]) => ({
                      logins,
                      days,
                    })),
                  )
                  .encode("x", "days")
                  .encode("y", "logins")
                  .scale("x", {
                    range: [0, 1],
                    min: 0,
                    max: 100,
                  })
                  .scale("y", {
                    domainMin: 0,
                    min: 0,
                    nice: true,
                  })
                  .axis("y", {
                    title: "Logins",
                  })
                  .axis("x", {
                    title: "Days",
                  })
                  .line()
                  .label({
                    text: "logins",
                    style: {
                      dx: -4,
                      dy: -10,
                    },
                  });
                chart.point().style("fill", "white").tooltip(false);
              }}
            />
          </Row>
        )}
      </View>
      <Modal
        open={["delete"].includes(props.mode) && props.open}
        centered
        title={createMode() + " User"}
        footer={null}
        maskClosable={false}
        onCancel={onClose}
      >
        <Col className="flex flex-col overflow-hidden overflow-y-auto">
          {props.mode === "delete" && (
            <Typography.Paragraph>
              <Typography.Text>
                Continue deleting the{" "}
                <Typography.Text className="font-bold">
                  {item?.userName}
                </Typography.Text>{" "}
                user?
              </Typography.Text>
            </Typography.Paragraph>
          )}
          {error && (
            <Alert className="mb-2" message={errorMessage} type="error" />
          )}
          {/* Actions */}
          <Space className="sticky bottom-0 flex flex-row items-center justify-end">
            <Button disabled={loading} onClick={onClose}>
              Cancel
            </Button>
            <Button
              type={"primary"}
              danger={props.mode === "delete"}
              loading={loading}
              onClick={onSubmit}
            >
              {createMode()}
            </Button>
          </Space>
        </Col>
      </Modal>
      {/* NOTE: Will need it for future use */}
      {/* <AssignRewardFormDialog
        open={inputOpen && inputMode == "assignReward"}
        initialValues={{} as any}
        target={{
          type: "user",
          uid: item?.uid!,
        }}
        onFinish={onInputClose}
        onCancel={onInputClose}
      />
      <CalendarEventDialog
        mode={"add"}
        open={inputOpen && inputMode == "scheduleEvent"}
        initialValues={{} as any}
        target={{
          type: "user",
          uid: item?.uid!,
        }}
        onFinish={onInputClose}
        onCancel={onInputClose}
      />
      <ResetUserFormDialog
        open={inputOpen && inputMode == "resetPassword"}
        initialValues={{
          userUid: item?.uid!,
          userName: item?.userName!,
        }}
        onFinish={onInputClose}
        onCancel={onInputClose}
      /> */}
    </>
  );
}

export default CompanyUserView;
